/**
 * PsbStage6cPage — Route B ALLOFILE Adjustments Import
 *
 * Step 1: Source  — select an applied Stage 6B session (chain: S0→S1→S2→S6B)
 * Step 2: Bridge  — ALLOFILE class breakdown: allowance / management / wastage / held /
 *                   skip_empty; subtotal per class; wastage-default and classes-not-collapsed notices
 * Step 3: Review  — per-row staging rows grouped by class (tabs: Importable/Held/All + class filter)
 * Step 4: Apply   — write session_adjustment_entries; by-class provenance ledger
 *
 * Evidence gates enforced at this layer:
 *  - Raw source preservation: name, value, group_code, is_allow, is_perc, percent_val, SHA
 *  - Separate classes: allowance (include=True), management (include=False), wastage (include=False)
 *  - Wastage default: Exclude from Result (include_in_result=False) shown explicitly
 *  - Classes NOT collapsed to net
 *  - Ambiguous ISALLOW → held (exception register — never auto-inferred)
 *  - Template slots (empty NAME) → skip_empty (not held, not posted)
 *  - Precedence: existing matching adj_entry → skipped_existing
 *  - Draft + Route B + site 271 + source-fingerprint guards shown
 *  - Production gate: amber warning banner
 *  - Idempotency: repeat apply = 0 new rows
 *  - URL persistence: ?planId= restores step/plan
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, ChevronRight, ClipboardList, Database,
  Loader2, ShieldAlert, AlertTriangle, BadgeCheck, Layers,
  BarChart3, Info,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../services/api';

// ── Constants ────────────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Source' },
  { id: 2, label: 'Bridge' },
  { id: 3, label: 'Review' },
  { id: 4, label: 'Apply' },
];

const CLASS_META = {
  allowance:  { label: 'Allowance',        colour: 'emerald', incl: true,  desc: 'ISALLOW=True  — Include in Result' },
  management: { label: 'Management/Surplus', colour: 'amber',   incl: false, desc: 'ISALLOW=False — Exclude from Result' },
  wastage:    { label: 'Wastage',           colour: 'orange',  incl: false, desc: 'Keyword match  — Exclude from Result (default)' },
  held:       { label: 'Held',             colour: 'slate',   incl: false, desc: 'Ambiguous ISALLOW — exception register' },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtGBP(v) {
  if (v == null || v === '' || v === 'None') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ClassBadge({ cls }) {
  const meta = CLASS_META[cls] || { label: cls, colour: 'slate' };
  const colMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    orange:  'bg-orange-50 text-orange-700 border-orange-200',
    slate:   'bg-slate-100 text-slate-600',
  };
  return <Badge className={`${colMap[meta.colour] || colMap.slate} text-[10px]`}>{meta.label}</Badge>;
}

function InclBadge({ incl }) {
  return incl
    ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Include</Badge>
    : <Badge className="bg-slate-100 text-slate-600 text-[10px]">Exclude</Badge>;
}

// ── Step nav ─────────────────────────────────────────────────────────────────

function StepNav({ current }) {
  return (
    <ol className="flex items-center gap-2 text-sm mb-6" data-testid="step-nav-s6c">
      {STEPS.map((s, idx) => {
        const done = s.id < current;
        const active = s.id === current;
        return (
          <React.Fragment key={s.id}>
            <li
              data-testid={`step-nav-s6c-${s.id}`}
              className={`flex items-center gap-1.5 font-medium ${active ? 'text-slate-900' : done ? 'text-emerald-600' : 'text-slate-400'}`}
            >
              {done
                ? <CheckCircle2 size={15} className="text-emerald-500" />
                : <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs border ${active ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-400'}`}>{s.id}</span>
              }
              {s.label}
            </li>
            {idx < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300" />}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function RecBadge({ ok, label }) {
  return ok
    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200"><CheckCircle2 size={12} />{label || 'Pass'}</span>
    : <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full border border-red-200"><AlertTriangle size={12} />{label || 'Fail'}</span>;
}

function Stat({ label, value, mono, highlight, testid }) {
  const colour = highlight === 'green' ? 'text-emerald-700' : highlight === 'amber' ? 'text-amber-700' : highlight === 'orange' ? 'text-orange-700' : 'text-slate-900';
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-slate-500">{label}</span>
      <span data-testid={testid} className={`text-sm font-semibold ${colour} ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ── WriteGateCard (6C) ───────────────────────────────────────────────────────

function WriteGateCard({ bridge }) {
  if (!bridge) return null;
  const ok = bridge.write_gate_ok === true;
  const env = bridge.psb_write_env || '<unset>';
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
      data-testid="s6c-write-gate-card">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Environment Write Gate (independent of site ID)</p>
      <div className="flex items-center gap-3">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-red-700 bg-red-50 border-red-200'}`}>
          {ok ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
          {ok ? `PSB_WRITE_ENV=${env} — writes permitted` : `PSB_WRITE_ENV=${env} — writes BLOCKED`}
        </span>
        <span className="text-xs text-slate-500">Site ID check is a second, separate guard.</span>
      </div>
      {!ok && (
        <p className="text-xs text-red-700 mt-1">
          Set PSB_WRITE_ENV=preview to allow writes. Production writes are blocked regardless of site ID.
        </p>
      )}
    </div>
  );
}

// ── FingerprintChain (6C) ─────────────────────────────────────────────────────

function FingerprintChain({ plan }) {
  if (!plan) return null;
  const labels = ['S0', 'S1', 'S2', 'S6B', 'S6C'];
  const ids = [plan.stage0_run_id, plan.s1_run_id, plan.s2_plan_id, plan.s6b_run_id, plan.run_id];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="s6c-fingerprint-chain">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Source Fingerprint Chain</p>
      <div className="flex items-center flex-wrap gap-1">
        {labels.map((lbl, i) => {
          const id = ids[i];
          const shortId = id ? String(id).slice(0, 8) : '—';
          return (
            <React.Fragment key={lbl}>
              <div className="flex flex-col items-center">
                <span className="text-[9px] font-semibold text-slate-500 uppercase">{lbl}</span>
                <code className="text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{shortId}</code>
              </div>
              {i < labels.length - 1 && <span className="text-slate-300 text-xs font-bold px-0.5">→</span>}
            </React.Fragment>
          );
        })}
        <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
          <CheckCircle2 size={12} />Chain bound
        </span>
      </div>
    </div>
  );
}

// ── BridgeCard ────────────────────────────────────────────────────────────────

function BridgeCard({ bridge }) {
  if (!bridge) return null;
  const b = bridge;
  return (
    <div className="space-y-4" data-testid="s6c-bridge-card">

      {/* Source summary */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">ALLOFILE Source</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Source rows (total)" value={b.source_rows} mono testid="bridge-source-rows" />
          <Stat label="Template slots skipped (empty NAME)" value={b.skip_empty} mono testid="bridge-skip-empty" />
          <Stat label="Held (ambiguous ISALLOW)" value={b.held_count} mono highlight={b.held_count > 0 ? 'amber' : undefined} testid="bridge-held-count" />
          <Stat label="Importable total"
            value={(b.importable_allowance_count || 0) + (b.importable_management_count || 0) + (b.importable_wastage_count || 0)}
            mono highlight="green" testid="bridge-importable-total" />
        </div>
      </div>

      {/* Class breakdown */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Class Breakdown (separate — not collapsed)</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" data-testid="s6c-class-breakdown-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Class</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Rule</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">Include in Result</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600">Row count</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600">Total value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-1.5"><ClassBadge cls="allowance" /></td>
                <td className="px-3 py-1.5 text-slate-500 text-[11px]">ISALLOW=True</td>
                <td className="px-3 py-1.5 text-center"><InclBadge incl={true} /></td>
                <td className="px-3 py-1.5 text-right font-mono" data-testid="bridge-allow-count">{b.importable_allowance_count}</td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700 font-semibold" data-testid="bridge-allow-total">{fmtGBP(b.total_allowance_value)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-1.5"><ClassBadge cls="management" /></td>
                <td className="px-3 py-1.5 text-slate-500 text-[11px]">ISALLOW=False</td>
                <td className="px-3 py-1.5 text-center"><InclBadge incl={false} /></td>
                <td className="px-3 py-1.5 text-right font-mono" data-testid="bridge-mgmt-count">{b.importable_management_count}</td>
                <td className="px-3 py-1.5 text-right font-mono text-amber-700 font-semibold" data-testid="bridge-mgmt-total">{fmtGBP(b.total_management_value)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-1.5"><ClassBadge cls="wastage" /></td>
                <td className="px-3 py-1.5 text-slate-500 text-[11px]">Keyword match</td>
                <td className="px-3 py-1.5 text-center">
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-200">
                    Exclude (default)
                  </span>
                </td>
                <td className="px-3 py-1.5 text-right font-mono" data-testid="bridge-wastage-count">{b.importable_wastage_count}</td>
                <td className="px-3 py-1.5 text-right font-mono text-orange-700 font-semibold" data-testid="bridge-wastage-total">{fmtGBP(b.total_wastage_value)}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td className="px-3 py-2 font-semibold text-slate-700" colSpan={4}>Net effect (allowance − management − wastage)</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-slate-900" data-testid="bridge-net-effect">{fmtGBP(b.net_effect_allow_minus_surplus)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Notices */}
      <div className="space-y-2">
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 flex items-start gap-2">
          <Info size={13} className="text-orange-600 mt-0.5 shrink-0" />
          <p className="text-xs text-orange-700">
            <strong>Wastage default:</strong> include_in_result=<strong>False</strong> (Exclude from Result). Overridable only at apply time — not auto-inferred.
          </p>
        </div>
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-start gap-2">
          <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700">
            <strong>Classes not collapsed:</strong> each row is written as a separate session_adjustment_entry. Net is never posted directly.
          </p>
        </div>
        {b.held_count > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={13} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-700">
              {b.held_count} row{b.held_count !== 1 ? 's' : ''} held in exception register (ambiguous ISALLOW — not auto-inferred).
            </p>
          </div>
        )}
      </div>

      {/* ── Evidence Gap 3: ALLOFILE Field Inventory + Cost/Retail Absence ── */}
      {b.allofile_fields && b.allofile_fields.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="s6c-allofile-fields-section">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
            ALLOFILE Source Field Inventory (Raw Preservation Evidence)
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse" data-testid="s6c-allofile-fields-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">ALLOFILE Field</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Stored As</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Type</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Note</th>
                </tr>
              </thead>
              <tbody>
                {b.allofile_fields.map((f, i) => (
                  <tr key={f.field} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                    <td className="px-3 py-1.5 font-mono font-semibold text-slate-800">{f.field}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-600">{f.stored_as}</td>
                    <td className="px-3 py-1.5 text-slate-500">{f.type}</td>
                    <td className="px-3 py-1.5 text-slate-500 text-[11px]">{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Explicit cost/retail absence notice */}
          {b.cost_retail_note && (
            <div className="mt-3 rounded-lg bg-slate-100 border border-slate-300 px-3 py-2 flex items-start gap-2" data-testid="s6c-cost-retail-notice">
              <AlertTriangle size={13} className="text-slate-500 mt-0.5 shrink-0" />
              <p className="text-xs text-slate-700 font-medium">
                <strong>Cost / Retail Price fields:</strong> {b.cost_retail_note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SessionGuards ─────────────────────────────────────────────────────────────

function SessionGuards({ plan }) {
  if (!plan) return null;
  const guards = [
    { label: 'Status', value: plan.session_status || '—', ok: (plan.session_status || '').toLowerCase() === 'draft' },
    { label: 'Route', value: plan.period_to ? `Route B → ${plan.period_to}` : 'Route A', ok: !!plan.period_to },
    { label: 'Site', value: `Site ${plan.target_site_id}`, ok: plan.target_site_id === 271 },
    { label: 'S0 fingerprint', value: plan.stage0_run_id ? plan.stage0_run_id.slice(0, 8) : '—', ok: !!plan.stage0_run_id },
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="s6c-session-guards">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Session Guards</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {guards.map(g => (
          <div key={g.label} className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-500">{g.label}</span>
            <span className="text-xs font-mono text-slate-700">{g.value}</span>
            <RecBadge ok={g.ok} label={g.ok ? '✓' : '✗'} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── RowTable ──────────────────────────────────────────────────────────────────

function RowTable({ rows }) {
  if (!rows || rows.length === 0)
    return <p className="text-sm text-slate-400 py-4">No rows in this category.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6c-row-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">BAR</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Value</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Group / Cost</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">ISALLOW</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Class</th>
            <th className="text-center px-3 py-2 font-semibold text-slate-600">Include</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Status</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Hold reason</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">SHA</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono">{r.bar}</td>
              <td className="px-3 py-1.5 text-slate-700 font-medium">{r.name}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtGBP(r.value)}</td>
              <td className="px-3 py-1.5 font-mono text-slate-500 text-[10px]">{r.group_code || '—'}</td>
              <td className="px-3 py-1.5">
                {r.is_allow === true
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">True</Badge>
                  : r.is_allow === false
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">False</Badge>
                    : <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]">Ambiguous</Badge>
                }
              </td>
              <td className="px-3 py-1.5"><ClassBadge cls={r.adjustment_class} /></td>
              <td className="px-3 py-1.5 text-center"><InclBadge incl={r.include_in_result} /></td>
              <td className="px-3 py-1.5">
                {r.resolution_status === 'importable'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Importable</Badge>
                  : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Held</Badge>
                }
              </td>
              <td className="px-3 py-1.5 text-slate-400 text-[10px] max-w-[120px] truncate" title={r.hold_reason}>{r.hold_reason || '—'}</td>
              <td className="px-3 py-1.5 font-mono text-slate-400 text-[10px]">{r.source_sha256 || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── LinkTable ─────────────────────────────────────────────────────────────────

function LinkTable({ links }) {
  if (!links || links.length === 0)
    return <p className="text-sm text-slate-400">No provenance links recorded.</p>;

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-xs border-collapse" data-testid="s6c-link-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">adj_entry_id</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Class</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Name</th>
            <th className="text-right px-3 py-2 font-semibold text-slate-600">Value</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">ISALLOW</th>
            <th className="text-center px-3 py-2 font-semibold text-slate-600">Include</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Precedence</th>
            <th className="text-left px-3 py-2 font-semibold text-slate-600">Written at</th>
          </tr>
        </thead>
        <tbody>
          {links.map((lk, i) => (
            <tr key={lk.id || i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
              <td className="px-3 py-1.5 font-mono">{lk.adj_entry_id}</td>
              <td className="px-3 py-1.5"><ClassBadge cls={lk.adjustment_class} /></td>
              <td className="px-3 py-1.5 text-slate-700 font-medium">{lk.name}</td>
              <td className="px-3 py-1.5 text-right font-mono font-semibold">{fmtGBP(lk.value)}</td>
              <td className="px-3 py-1.5">
                {lk.is_allow === true
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">True</Badge>
                  : lk.is_allow === false
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">False</Badge>
                    : <span className="text-slate-400">—</span>
                }
              </td>
              <td className="px-3 py-1.5 text-center"><InclBadge incl={lk.include_in_result} /></td>
              <td className="px-3 py-1.5">
                {lk.precedence_result === 'written'
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Written</Badge>
                  : lk.precedence_result === 'skipped_existing'
                    ? <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Existing wins</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 text-[10px]">{lk.precedence_result}</Badge>
                }
              </td>
              <td className="px-3 py-1.5 text-slate-400 text-[10px] font-mono">{lk.written_at || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PsbStage6cPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);

  // Step 2/3/4
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [rowCounts, setRowCounts] = useState({});
  const [byClass, setByClass] = useState({});
  const [links, setLinks] = useState([]);

  // Step 3 filters
  const [reviewTab, setReviewTab] = useState('importable');
  const [classFilter, setClassFilter] = useState('all');

  // Step 4
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);

  const planIdParam = searchParams.get('planId');
  // Guard: skip URL-restore override when we're in the middle of handleCreatePlan
  const isCreatingRef = React.useRef(false);

  // ── Load plan detail helper ──────────────────────────────────────────────────
  const loadPlanDetail = useCallback(async (planId, category = 'importable') => {
    const d = await api.get(`/admin/psb-stage6c/plans/${planId}?category=${category}`);
    setPlan(d.data.plan);
    setRows(d.data.rows || []);
    setRowCounts(d.data.row_counts || {});
    setByClass(d.data.by_class || {});
    setLinks(d.data.links || []);
    return d.data;
  }, []);

  // ── Restore from URL ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!planIdParam) return;
    if (isCreatingRef.current) return;   // skip: handleCreatePlan is setting the URL
    (async () => {
      try {
        const d = await loadPlanDetail(planIdParam, 'all');
        if (d.plan.status === 'applied') {
          setReviewTab('all');
          setStep(4);
        } else {
          setStep(3);
        }
      } catch {
        /* plan not found */
      }
    })();
  }, [planIdParam, loadPlanDetail]);

  // ── Step 1: load sessions ────────────────────────────────────────────────────
  useEffect(() => {
    if (step !== 1) return;
    (async () => {
      setLoading(true);
      try {
        const d = await api.get('/admin/psb-stage6c/s6b-sessions');
        setSessions(d.data.sessions || []);
      } catch {
        toast.error('Failed to load Stage 6B sessions');
      } finally {
        setLoading(false);
      }
    })();
  }, [step]);

  // ── Step 3: reload rows on tab/class change ──────────────────────────────────
  useEffect(() => {
    if (step !== 3 || !plan) return;
    (async () => {
      try {
        const d = await api.get(`/admin/psb-stage6c/plans/${plan.run_id}?category=${reviewTab}`);
        setRows(d.data.rows || []);
        setRowCounts(d.data.row_counts || rowCounts);
        setByClass(d.data.by_class || byClass);
        setLinks(d.data.links || []);
      } catch {
        toast.error('Failed to load rows');
      }
    })();
  }, [step, plan, reviewTab]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleCreatePlan = useCallback(async () => {
    if (!selectedSession) return;
    setLoading(true);
    try {
      const d = await api.post('/admin/psb-stage6c/plans', {
        stage0_run_id: selectedSession.stage0_run_id,
        s1_run_id: selectedSession.s1_run_id,
        s2_plan_id: selectedSession.s2_plan_id,
        s6b_run_id: selectedSession.s6b_run_id,
        target_session_id: selectedSession.target_session_id,
        site_id: selectedSession.target_site_id,
      });
      await loadPlanDetail(d.data.plan.run_id, 'importable');
      if (d.data.idempotent) toast.info('Existing dry-run plan loaded (idempotent)');
      // Set flag BEFORE setSearchParams so URL-restore useEffect skips the override
      isCreatingRef.current = true;
      setSearchParams({ planId: d.data.plan.run_id });
      setPlan(prev => prev ? { ...prev, session_status: selectedSession.session_status, period_to: selectedSession.period_to } : prev);
      setStep(2);
      // Clear flag after next tick
      setTimeout(() => { isCreatingRef.current = false; }, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    } finally {
      setLoading(false);
    }
  }, [selectedSession, loadPlanDetail, setSearchParams]);

  const handleTabChange = useCallback((tab) => {
    setReviewTab(tab);
    setClassFilter('all');
  }, []);

  const handleApply = useCallback(async () => {
    if (!plan) return;
    setApplying(true);
    try {
      const d = await api.post(`/admin/psb-stage6c/plans/${plan.run_id}/apply`);
      setApplyResult(d.data);
      setLinks(d.data.links || []);
      setPlan(prev => prev ? { ...prev, status: 'applied' } : prev);
      setStep(4);
      const written = d.data.written || 0;
      if (d.data.idempotent) {
        toast.info('Already applied — no new rows (idempotent)');
      } else {
        toast.success(`Stage 6C applied — ${written} adjustment entr${written !== 1 ? 'ies' : 'y'} written`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [plan]);

  // ── Filtered rows for display ─────────────────────────────────────────────────
  const displayRows = classFilter === 'all'
    ? rows
    : rows.filter(r => r.adjustment_class === classFilter);

  // ── Importable count from row_counts ─────────────────────────────────────────
  const importableCount = rowCounts.importable || 0;
  const heldCount = rowCounts.held || 0;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Layers size={20} className="text-indigo-600" />
            PSB Stage 6C — ALLOFILE Adjustments Import
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Import ALLOFILE allowances/adjustments into session_adjustment_entries · Preview only · Site 271
          </p>
        </div>
      </div>

      <StepNav current={step} />

      {/* Step 1 — Source */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database size={16} className="text-indigo-500" />Select Stage 6B Source Session
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              Select an applied Stage 6B session. The full S0→S1→S2→S6B fingerprint chain will be validated before the ALLOFILE plan is created.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <ShieldAlert size={15} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">Preview only — writes gated to site 271. Production writes remain disabled.</p>
            </div>

            {loading && <div className="flex items-center gap-2 text-slate-400 text-sm py-2"><Loader2 size={16} className="animate-spin" />Loading sessions…</div>}

            {!loading && sessions.length === 0 && (
              <p className="text-sm text-slate-400">No applied Stage 6B sessions found. Complete Stage 6B first.</p>
            )}

            {!loading && sessions.map(s => (
              <div
                key={s.s6b_run_id}
                onClick={() => setSelectedSession(s)}
                className={`cursor-pointer rounded-xl border p-4 transition-all ${selectedSession?.s6b_run_id === s.s6b_run_id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                data-testid={`session-card-${s.target_session_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{s.session_reference}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Session {s.target_session_id} · Site {s.target_site_id} · {s.period_from} → {s.period_to}</p>
                    <p className="text-xs text-slate-400 mt-0.5 font-mono">S6B: {s.s6b_run_id.slice(0, 8)} · S6A: {s.s6a_run_id.slice(0, 8)}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">S6B Applied</Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-[10px]">{s.session_status}</Badge>
                    {s.period_to && <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">Route B</Badge>}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleCreatePlan}
                disabled={!selectedSession || loading}
                data-testid="create-plan-btn"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                {loading ? <><Loader2 size={15} className="animate-spin mr-1" />Building plan…</> : <>Run dry-run <ChevronRight size={15} className="ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2 — Bridge */}
      {step === 2 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BarChart3 size={16} className="text-indigo-500" />ALLOFILE Class Bridge
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Plan:</span>
              <code className="bg-slate-100 rounded px-1.5 py-0.5 font-mono" data-testid="plan-id-display">{plan.run_id.slice(0, 8)}</code>
              <span>→ Session {plan.target_session_id} · Site {plan.target_site_id}</span>
              {plan.stage0_run_id && <>
                <span>·</span>
                <span className="font-mono text-slate-400">S0: {plan.stage0_run_id.slice(0, 8)}</span>
              </>}
            </div>

            <SessionGuards plan={{ ...plan, session_status: selectedSession?.session_status, period_to: selectedSession?.period_to }} />
            <BridgeCard bridge={plan.bridge} />

            {/* ── Evidence Gap 1: Write Gate ─────────────────────────────── */}
            <WriteGateCard bridge={plan.bridge} />

            {/* ── Evidence Gap 2: S0 Fingerprint Chain ───────────────────── */}
            <FingerprintChain plan={plan} />

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setPlan(null); setSearchParams({}); }} data-testid="back-to-source-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              <Button onClick={() => { setStep(3); setReviewTab('importable'); setClassFilter('all'); }} data-testid="proceed-to-review-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Review rows <ChevronRight size={15} className="ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Review */}
      {step === 3 && plan && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <ClipboardList size={16} className="text-indigo-500" />Review ALLOFILE Rows
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tab bar */}
            <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
              {[
                { key: 'importable', label: `Importable (${importableCount})`, colour: 'emerald' },
                { key: 'held',       label: `Held (${heldCount})`,             colour: 'amber' },
                { key: 'all',        label: `All (${(importableCount + heldCount)})`, colour: 'slate' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`text-xs font-medium px-3 py-1.5 rounded-md transition-all ${
                    reviewTab === t.key
                      ? `bg-${t.colour}-50 text-${t.colour}-700 border border-${t.colour}-200`
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Class filter (only for importable / all) */}
            {(reviewTab === 'importable' || reviewTab === 'all') && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Filter by class:</span>
                {['all', 'allowance', 'management', 'wastage'].map(cls => (
                  <button
                    key={cls}
                    onClick={() => setClassFilter(cls)}
                    data-testid={`class-filter-${cls}`}
                    className={`text-xs px-2 py-1 rounded border transition-all ${classFilter === cls ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}
                  >
                    {cls === 'all' ? 'All classes' : CLASS_META[cls]?.label || cls}
                  </button>
                ))}
              </div>
            )}

            <RowTable rows={displayRows} />

            <div className="flex items-center gap-4 text-xs text-slate-500 pt-1">
              <span data-testid="display-rows-count">Showing {displayRows.length} row{displayRows.length !== 1 ? 's' : ''}</span>
              {Object.entries(byClass).map(([cls, cnt]) => (
                <span key={cls}>{CLASS_META[cls]?.label || cls}: {cnt}</span>
              ))}
            </div>

            <div className="flex gap-3 justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="back-to-bridge-btn">
                <ArrowLeft size={15} className="mr-1" />Back
              </Button>
              {plan.status === 'applied' ? (
                <Button onClick={() => setStep(4)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="view-apply-result-btn">
                  <CheckCircle2 size={15} className="mr-1" />View result
                </Button>
              ) : (
                <Button
                  onClick={handleApply}
                  disabled={applying || importableCount === 0}
                  data-testid="apply-btn"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  {applying ? <><Loader2 size={15} className="animate-spin mr-1" />Applying…</> : <>Apply Stage 6C <ChevronRight size={15} className="ml-1" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Apply result */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck size={16} className="text-emerald-500" />Stage 6C Applied
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">

            {/* Summary */}
            {applyResult && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 space-y-3" data-testid="s6c-apply-result">
                <p className="text-sm font-semibold text-emerald-800">
                  Adjustments written to session {applyResult.target_session_id}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label="Entries written" value={applyResult.written} highlight={applyResult.written > 0 ? 'green' : undefined} testid="apply-written" />
                  <Stat label="Existing wins (skipped)" value={applyResult.skipped_existing} testid="apply-skipped" />
                  {applyResult.by_class_totals?.allowance != null && (
                    <Stat label="Allowance total" value={fmtGBP(applyResult.by_class_totals.allowance)} mono highlight="green" testid="apply-allowance-total" />
                  )}
                  {applyResult.by_class_totals?.management != null && (
                    <Stat label="Management total" value={fmtGBP(applyResult.by_class_totals.management)} mono highlight="amber" testid="apply-mgmt-total" />
                  )}
                </div>
                {applyResult.idempotent && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100" data-testid="idempotent-notice">
                    Idempotent — plan was already applied. Zero new rows written.
                  </p>
                )}
              </div>
            )}

            {/* Stored values from plan bridge (URL restore case) */}
            {!applyResult && plan?.bridge && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-700">Bridge recap (restored from plan)</p>
                <BridgeCard bridge={plan.bridge} />
              </div>
            )}

            {/* Provenance ledger */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                Provenance Ledger — session_adjustment_entries
              </p>
              <LinkTable links={links} />
            </div>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(3)} data-testid="back-to-review-btn">
                <ArrowLeft size={15} className="mr-1" />Review
              </Button>
              <Button variant="outline" onClick={() => navigate('/stocktaking/sessions')} data-testid="view-session-btn">
                View session
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
